import type { FC, FormEvent, ReactNode } from 'react'
import type { FormStatus } from '@/types'

import Link from 'next/link'
import { useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { isEmail } from '@/lib/forms'

export const NewsletterForm: FC = (): ReactNode => {
    const [email, setEmail] = useState('')
    const [company, setCompany] = useState('')
    const [status, setStatus] = useState<FormStatus>('idle')
    const [message, setMessage] = useState('')

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const value = email.trim()

        if (!isEmail(value)) {
            setStatus('error')
            setMessage('Please enter a valid email address.')

            return
        }

        setStatus('submitting')
        setMessage('')

        try {
            const response = await fetch('/api/newsletter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: value, company })
            })

            const payload = await response.json().catch(() => null) as { message?: string } | null

            if (!response.ok) throw new Error(payload?.message || 'Something went wrong. Please try again.')

            setStatus('success')
            setEmail('')
            setMessage('Thank you. News from the Maison will reach you soon.')
        }

        catch (error) {
            setStatus('error')
            setMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.')
        }
    }

    return (
        <form onSubmit={onSubmit} noValidate className='mt-8 max-w-md'>
            <label htmlFor='newsletter-email' className='sr-only'>
                Email address
            </label>

            <div className='flex items-center border-b border-background/40 transition-colors focus-within:border-background'>
                <input
                    id='newsletter-email'
                    type='email'
                    name='email'
                    autoComplete='email'
                    placeholder='Your email address'
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                    aria-invalid={status === 'error'}
                    aria-describedby='newsletter-message'
                    className='h-12 flex-1 bg-transparent text-[15px] text-background outline-none placeholder:text-background/50'
                />

                <button
                    type='submit'
                    disabled={status === 'submitting'}
                    className='flex h-12 items-center gap-2 pl-4 text-[0.6875rem] font-medium uppercase tracking-[0.22em] text-background transition-opacity hover:opacity-70 disabled:opacity-50'
                >
                    {status === 'submitting' ? 'Sending' : 'Subscribe'}
                    <ArrowRight className='size-4' strokeWidth={1.25} aria-hidden />
                </button>
            </div>

            <div aria-hidden className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                <label htmlFor='newsletter-company'>
                    Company
                </label>

                <input
                    id='newsletter-company'
                    type='text'
                    name='company'
                    tabIndex={-1}
                    autoComplete='off'
                    value={company}
                    onChange={event => setCompany(event.target.value)}
                />
            </div>

            <p id='newsletter-message' role='status' className='mt-3 min-h-5 text-sm text-background/80'>
                {message}
            </p>

            <p className='mt-4 text-xs leading-relaxed text-background/60'>
                By subscribing you agree to receive news from Maison Vaurel. You can unsubscribe at any time. Read our{' '}

                <Link href='/privacy' className='underline underline-offset-4 hover:text-background'>
                    privacy policy
                </Link>
                .
            </p>
        </form>
    )
}
